'use client';

import type { ChangeEvent, KeyboardEvent } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@/libs/tailwind';

export type Option = {
  label: string;
  value: string;
};

type MultiSelectProps = {
  options: Option[];
  selectedValues?: string[];
  onValueChange?: (values: string[]) => void;
  onKeyDown?: (e: KeyboardEvent) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  maxDisplayItems?: number;
  focusElement?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  showPills?: boolean;
  closeOnSelect?: boolean;
  isLoading?: boolean;
  autoFocusOnSearch?: boolean;
  loadingPlaceholder?: string;
  onSearchChange?: (query: string) => void;
};

export const MultiSelect = ({
  options,
  selectedValues,
  onValueChange,
  onKeyDown,
  placeholder = 'Select options...',
  disabled = false,
  className,
  triggerClassName,
  contentClassName,
  maxDisplayItems = 2,
  focusElement = false,
  searchable = false,
  searchPlaceholder = 'Search...',
  showPills = false,
  closeOnSelect = false,
  isLoading = false,
  autoFocusOnSearch = false,
  loadingPlaceholder = 'Loading...',
  onSearchChange,
}: MultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const isControlled = selectedValues !== undefined;
  const [internalSelectedValues, setInternalSelectedValues] = useState<string[]>([]);
  const selected = isControlled ? selectedValues : internalSelectedValues;

  const filteredOptions = !onSearchChange && searchQuery
    ? options.filter(option => option.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : options;

  useEffect(() => {
    if (isControlled) {
      setInternalSelectedValues(selectedValues);
    }
  }, [isControlled, selectedValues]);

  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setFocusedIndex(null);
    } else if (open && searchable && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [open, searchable]);

  const handleCheckedChange = (value: string, checked: boolean) => {
    let updatedValues: string[];

    if (checked) {
      updatedValues = [...selected, value];
    } else {
      updatedValues = selected.filter(v => v !== value);
    }

    if (!isControlled) {
      setInternalSelectedValues(updatedValues);
    }

    if (onValueChange) {
      onValueChange(updatedValues);
    }

    if (closeOnSelect) {
      setOpen(false);
    }
  };

  const handleInternalSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setSearchQuery(newQuery);
    if (onSearchChange) {
      onSearchChange(newQuery);
    }
  };

  const handleRemoveValue = (valueToRemove: string) => {
    const updatedValues = selected.filter(value => value !== valueToRemove);

    if (!isControlled) {
      setInternalSelectedValues(updatedValues);
    }

    if (onValueChange) {
      onValueChange(updatedValues);
    }
  };

  const displayValue = useMemo(() => {
    if (!selected || selected.length === 0) {
      return placeholder;
    }

    if (!showPills) {
      if (selected.length === 1) {
        const option = options.find(opt => opt.value === selected[0]);
        return option?.label || selected[0];
      }

      const selectedLabels = selected
        .map((value) => {
          const option = options.find(opt => opt.value === value);
          return option?.label || value;
        })
        .filter(Boolean);

      if (selectedLabels.length <= maxDisplayItems) {
        return selectedLabels.join(', ');
      } else {
        const displayedItems = selectedLabels.slice(0, maxDisplayItems);
        const remainingCount = selectedLabels.length - maxDisplayItems;
        return `${displayedItems.join(', ')} ${remainingCount > 0 ? `+${remainingCount} more` : ''}`;
      }
    }

    return open ? placeholder : '';
  }, [selected, options, placeholder, maxDisplayItems, showPills, open]);

  const handleSearchKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => prev === null ? 0 : Math.min(prev + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => prev === null ? filteredOptions.length - 1 : Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (focusedIndex !== null) {
        const option = filteredOptions[focusedIndex] as Option;
        handleCheckedChange(option.value, !selected?.includes(option.value));
      }
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (focusedIndex !== null && open) {
      const focusedOption = document.querySelector(`[data-index="${focusedIndex}"]`);
      focusedOption?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex, open]);

  return (
    <div className={cn('relative p-[1px]', showPills ? 'mb-1' : 'mb-4', className)}>
      <DropdownMenuPrimitive.Root open={open} onOpenChange={setOpen}>
        <DropdownMenuPrimitive.Trigger asChild disabled={disabled}>
          <button
            onKeyDown={onKeyDown}
            ref={(input) => {
              focusElement && input?.focus();
            }}
            type="button"
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-lg border border-fg-border bg-bg-surface-primary px-3 py-2 text-body-md text-fg-primary disabled:cursor-not-allowed disabled:opacity-50 gap-1',
              triggerClassName,
            )}
            aria-expanded={open}
          >
            <span className={cn('flex-grow truncate text-left text-body-md', showPills && !selected?.length && 'text-fg-primary')}>
              {displayValue}
            </span>
            <div className="flex items-center">
              <ChevronDown className="size-6" />
            </div>
          </button>
        </DropdownMenuPrimitive.Trigger>

        <DropdownMenuPrimitive.Portal>
          <DropdownMenuPrimitive.Content
            align="start"
            className={cn(
              'relative z-50 max-h-96 min-w-[var(--radix-dropdown-menu-trigger-width)] overflow-hidden rounded-lg border-thin bg-bg-surface-primary py-2 text-fg-primary shadow-md backdrop-blur-[16px] focus:outline-none mt-1 mb-1',
              contentClassName,
            )}
            onKeyDown={handleSearchKeyDown} // Attach the handler here
          >
            {searchable && (
              <div className="px-2 pb-2">
                <div className="relative">
                  <input
                    /* eslint-disable-next-line jsx-a11y/no-autofocus */
                    autoFocus={autoFocusOnSearch && open}
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={handleInternalSearchChange}
                    placeholder={searchPlaceholder}
                    className="w-full text-body-md text-fg-primary p-2 pl-8 rounded-md border-0.5 border-fg-border bg-bg-surface-primary focus:outline-none"
                    onKeyDown={handleSearchKeyDown}
                    onClick={e => e.stopPropagation()}
                  />
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 size-4 text-gray-400" />
                  {searchQuery && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchQuery('');
                        if (onSearchChange) {
                          onSearchChange('');
                        }
                        searchInputRef.current?.focus();
                      }}
                      type="button"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 size-4 text-gray-400 hover:text-gray-500 cursor-pointer"
                    >
                      <X className="size-4" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {isLoading ? (
              <div className="px-4 py-2 text-body-md text-gray-500">{loadingPlaceholder}</div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-4 py-2 text-body-md text-gray-500">
                {searchQuery ? 'No results found' : 'No options available'}
              </div>
            ) : (
              <div className="overflow-auto max-h-80">
                {filteredOptions.map((option, index) => (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                  <div
                    key={option.value}
                    data-index={index}
                    className={cn(
                      'h-10 relative flex cursor-default select-none items-center py-2 pl-12 pr-4 text-body-md outline-none hover:bg-interactive-hover text-fg-primary',
                    )}
                    onClick={() => handleCheckedChange(option.value, !selected?.includes(option.value))}
                    role="option"
                    tabIndex={0}
                    aria-selected={selected?.includes(option.value)}
                  >
                    <span className="absolute left-4 inline-flex items-center justify-center">
                      {selected?.includes(option.value) && (
                        <Check className="size-5" />
                      )}
                    </span>
                    <span className="text-body-md">{option.label}</span>
                  </div>
                ))}
              </div>
            )}
          </DropdownMenuPrimitive.Content>
        </DropdownMenuPrimitive.Portal>
      </DropdownMenuPrimitive.Root>

      {showPills && selected && selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selected.map((value) => {
            const option = options.find(opt => opt.value === value);
            return (
              <div
                key={value}
                className="flex items-center bg-gray-100 rounded-full pl-3 pr-1 py-1 text-body-md text-gray-700"
              >
                <span className="mr-1">{option?.label || value}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveValue(value);
                  }}
                  className="rounded-full hover:bg-gray-200 p-1"
                >
                  <X className="h-3 w-3 text-gray-500" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
